import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import Toastr from "../components/shared/Toastr";
import { ChainContext } from "./ChainContext";
import { expiryTimeOptions } from "../components/limit/LimitOrderExpiryTime";
import { ChainConfigContext } from "./ChainConfigContext";
import { DEFAULT_GAS_PRICE, DEFAULT_GAS_PRICE_ID, DEFAULT_SLIPPAGE } from "../config/constants";
import useDefaultTokens from "../hooks/useDefaultTokens";
import { isNativeToken } from "../utils/tokenUtils";
import { timeFrameOptions } from "../components/dca/DcaTimeFrame";
import chainList from "../config/chainList";

export interface ISwapForm {
  crossChain: boolean;
  chainIdFrom: number;
  chainIdTo: number;
  tokenAddressFrom: string;
  tokenAddressTo: string;
  amountFrom: number | undefined;
  amountTo: number | undefined;
  slippage: number;
  gasPriceId: string;
  gasPrice: number;
  crossChainSelectedRoute: number;
  limitPrice: number | undefined;
  limitExpiryTimeMiliseconds: number | undefined;
  type: string | undefined; // limit, swap
  isLimitPriceReversed: boolean;

  dcaFrequency?: number;
  dcaTimeFrame?: number;
  dcaOrders?: number;
  dcaEnablePricing?: boolean;
  dcaMinPrice?: number;
  dcaMaxPrice?: number;

  quoteData?: any;
  quoteCrossChainData?: any;

  allowNativeTokenFrom?: boolean;
  dca?: boolean;
}

export interface IFormContext {
  form: ISwapForm,
  onFormChange: (obj:any) => any,
  getInitialForm: () => ISwapForm
}

const FormContext = React.createContext<IFormContext>(
  {} as IFormContext,
);

const FormProvider = ({ children }) => {
  const { location } = typeof window !== 'undefined' ? window : { location: { pathname: '' } };
  const { selectedChainId, gasPriceOptions } = useContext(ChainContext);
  const { getTokensByChainId, supportedTokens, supportedCrossChains } = useContext(ChainConfigContext);
  const { getDefaultTokenFrom, getDefaultTokenTo } = useDefaultTokens();

  const getInitialForm = (): ISwapForm => {
    const isDca = location?.pathname.includes("/dca");
    const isCrossChain = location?.pathname.includes("/bridge");
    const allowNativeTokenFrom = location?.pathname.includes("limit") || location?.pathname.includes("dca") ? false : true;

    const chainIdFrom = selectedChainId;
    const chainIdTo = isCrossChain 
      ? (supportedCrossChains || supportedTokens?.[chainList?.filter(e => e?.chainId !== chainIdFrom)?.[0]?.chainId] || []).filter(e => e?.chainId != chainIdFrom)?.[0]?.chainId
      : chainIdFrom;

    let tokenFrom = getDefaultTokenFrom(chainIdFrom) || (supportedTokens?.[chainIdFrom]?.[0]);
    let tokenTo = getDefaultTokenTo(chainIdTo, tokenFrom?.symbol) || (supportedTokens?.[chainIdTo]?.[1]);

    if (!allowNativeTokenFrom && isNativeToken(tokenFrom?.address)) {
      tokenFrom = supportedTokens?.[chainIdFrom]?.filter(e => e.address !== tokenTo?.address && !isNativeToken(e.address))?.[0];
    }
    if (!allowNativeTokenFrom && isNativeToken(tokenTo?.address)) {
      tokenTo = supportedTokens?.[chainIdTo]?.filter(e => e.address !== tokenFrom?.address && !isNativeToken(e.address))?.[0];
    }

    return {
      crossChain: isCrossChain,
      chainIdFrom: chainIdFrom,
      chainIdTo: chainIdTo,
      tokenAddressFrom: tokenFrom?.address || "",
      tokenAddressTo: tokenTo?.address || "",
      amountFrom: 1,
      amountTo: undefined,
      slippage: DEFAULT_SLIPPAGE,
      gasPriceId: DEFAULT_GAS_PRICE_ID,
      gasPrice: DEFAULT_GAS_PRICE,
      crossChainSelectedRoute: 0,
      limitPrice: undefined,
      limitExpiryTimeMiliseconds: expiryTimeOptions[1].miliseconds,
      type: "swap",
      isLimitPriceReversed: false,
      allowNativeTokenFrom: allowNativeTokenFrom,
      dcaTimeFrame: timeFrameOptions[0].value,
      dca: isDca,
    };
  };

  const [currentForm, setCurrentForm] = useState<ISwapForm>(getInitialForm());

  // Update form when path changes
  useEffect(() => {
    setCurrentForm((prev) => ({ ...prev, ...getInitialForm() }));
  }, [supportedTokens?.length, supportedCrossChains?.length]);

  const onFormChange = (obj = {}) => {
    setCurrentForm((prev) => ({ ...prev, ...obj }));
  };

  // Get tokens by chain, only chains from and to if different
  useEffect(() => {
    [...new Set([currentForm.chainIdFrom, currentForm.chainIdTo])].map((chainId) => getTokensByChainId(chainId));
  }, [currentForm.chainIdFrom, currentForm.chainIdTo]);

  // Gas price
  useEffect(() => {
    const _gasPrice = gasPriceOptions?.find(e => e.id === currentForm.gasPriceId)?.gwei;
    if (_gasPrice) {
      onFormChange({ gasPrice: _gasPrice });
    }
  }, [gasPriceOptions, currentForm.gasPriceId]);

  // Native tokens not allowed
  useEffect(() => {
    if (
      !currentForm.allowNativeTokenFrom && 
      !!currentForm.tokenAddressFrom && 
      isNativeToken(currentForm.tokenAddressFrom)
    ) {
      const _tokenFrom = getDefaultTokenFrom(currentForm.chainIdFrom);
      onFormChange({ 
        tokenAddressFrom: _tokenFrom?.address || supportedTokens?.[currentForm.chainIdFrom]?.filter(e => e.address !== currentForm.tokenAddressTo)?.[0]?.address || "" 
      });
    }
  }, [currentForm.allowNativeTokenFrom, currentForm.tokenAddressFrom, supportedTokens?.[currentForm.chainIdFrom], currentForm.chainIdFrom]);

  // Can't allow same token from and to when chains are the same
  useEffect(() => {
    if (currentForm.chainIdFrom === currentForm.chainIdTo && currentForm.tokenAddressFrom === currentForm.tokenAddressTo) {
      const _tokenTo = supportedTokens?.[currentForm.chainIdTo]?.filter(e => e.address !== currentForm.tokenAddressTo)?.[0];
      onFormChange({ tokenAddressTo: _tokenTo?.address || "" });
    }
  }, [currentForm.chainIdFrom, currentForm.chainIdTo, currentForm.tokenAddressFrom, currentForm.tokenAddressTo, supportedTokens?.[currentForm.chainIdTo]]);

  // Default tokens
  // useEffect(() => {
  //   if (
  //     currentForm.chainIdFrom && 
  //     currentForm.chainIdTo && 
  //     supportedTokens !== undefined &&
  //     supportedTokens?.[currentForm.chainIdFrom]?.length > 0 &&
  //     supportedTokens?.[currentForm.chainIdTo]?.length > 0
  //   ) {
  //     const _tokenFrom = getDefaultTokenFrom(currentForm.chainIdFrom) || supportedTokens?.[currentForm.chainIdFrom]?.[0];
  //     let _tokenTo = getDefaultTokenTo(currentForm.chainIdTo, _tokenFrom?.symbol) || supportedTokens?.[currentForm.chainIdTo]?.[1];
  //     if (_tokenTo?.address === _tokenFrom?.address && currentForm.chainIdFrom === currentForm.chainIdTo) {
  //       _tokenTo = supportedTokens?.[currentForm.chainIdTo]?.filter(e => e.address !== _tokenFrom?.address)?.[0];
  //     }
  //     onFormChange({ 
  //       tokenAddressFrom: _tokenFrom?.address || "",
  //       tokenAddressTo: _tokenTo?.address || ""
  //     });
  //   }
  // }, [currentForm.chainIdFrom, currentForm.chainIdTo, supportedTokens?.[currentForm.chainIdFrom], supportedTokens?.[currentForm.chainIdTo]]);

  // Prevent empty tokenAddressFrom
  useEffect(() => {
    if (
      currentForm.chainIdFrom && 
      supportedTokens !== undefined &&
      supportedTokens?.[currentForm.chainIdFrom]?.length > 0 &&
      (!currentForm.tokenAddressFrom || supportedTokens?.[currentForm.chainIdFrom]?.find(e => e.address === currentForm.tokenAddressFrom) === undefined)
    ) {
      const defaultToken = getDefaultTokenFrom(currentForm.chainIdFrom) || supportedTokens?.[currentForm.chainIdFrom]?.[0];
      onFormChange({ tokenAddressFrom: defaultToken?.address || "" });
    }
  }, [currentForm.chainIdFrom, currentForm.tokenAddressFrom, supportedTokens?.[currentForm.chainIdFrom]]);

  // Prevent empty tokenAddressTo
  useEffect(() => {
    if (
      currentForm.chainIdTo && 
      supportedTokens !== undefined &&
      supportedTokens?.[currentForm.chainIdTo]?.length > 0 &&
      (!currentForm.tokenAddressTo || supportedTokens?.[currentForm.chainIdTo]?.find(e => e.address === currentForm.tokenAddressTo) === undefined)
    ) {
      const defaultToken = getDefaultTokenTo(currentForm.chainIdTo, currentForm.tokenAddressFrom) || supportedTokens?.[currentForm.chainIdTo]?.[0];
      onFormChange({ tokenAddressTo: defaultToken?.address || "" });
    }
  }, [currentForm.chainIdTo, currentForm.tokenAddressTo, supportedTokens?.[currentForm.chainIdTo]]);

  // // Prevent same chain when cross chain
  // useEffect(() => {
  //   if (
  //     currentForm.crossChain && 
  //     currentForm.chainIdFrom === currentForm.chainIdTo && 
  //     supportedCrossChains?.length > 0
  //   ) {
  //     const availableChains = supportedCrossChains.filter(chain => chain.chainId !== currentForm.chainIdFrom);
  //     if (availableChains.length > 0) {
  //       onFormChange({ chainIdTo: availableChains[0].chainId });
  //     }
  //   } else if (!currentForm.crossChain && currentForm.chainIdFrom !== currentForm.chainIdTo) {
  //     onFormChange({ chainIdTo: currentForm.chainIdFrom });
  //   }
  // }, [currentForm.crossChain, currentForm.chainIdFrom, currentForm.chainIdTo, supportedCrossChains]);

  return (
    <FormContext.Provider
      value={{ 
        form: currentForm, 
        onFormChange,
        getInitialForm 
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export { FormContext, FormProvider };
